import { useNavigate } from 'react-router-dom';
import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { noop } from 'lodash';

import { PatronSessionState } from '@app/components';
import { useAppContext } from '@app/contexts';
import { APIError, axios, setAxiosHeaders } from '@app/utils';
import { invokePathPrefix, Route, SetState, UserSession } from '@app/constants';

import { useQueryParams } from '../useQueryParams';

interface DirectLoginRequestBody {
  password: string;
  username: string;
}

interface LoginRequestBody {
  barcode: string;
  pin: string;
}

export interface UserInfo {
  externalSystemId: string;
}

export interface UserPersonalInfo {
  lastName: string;
  firstName: string;
  email: string;
}

export interface PatronSession {
  token: string;
  user?: UserInfo & Partial<UserPersonalInfo>;
}

export interface UserMetadata {
  user: { personal?: UserPersonalInfo } & UserInfo;
}

type processPatronSession = (
  token: string,
  data: UserMetadata,
  customRedirectRoute?: string
) => void;

interface LoginUtils {
  directLoginMutation: UseMutationResult;
  loginMutation: UseMutationResult;
  processPatronSession: processPatronSession;
}

interface UseLoginProps {
  isConsortiaLogin?: boolean;
  setPatronSession?: SetState<PatronSessionState>;
}

export const useLogin = ({
  isConsortiaLogin = false,
  setPatronSession = noop,
}: UseLoginProps = {}): LoginUtils => {
  const navigate = useNavigate();
  const { redirect } = useQueryParams();
  const appContext = useAppContext();

  const redirectRoute = (redirect as string) ?? Route.search;

  const handleError = error => {
    if (error.response.data) {
      const errorInfo = error.response.data;

      throw new APIError(
        errorInfo.errorMessage ||
          errorInfo.errors?.[0]?.message ||
          errorInfo.message ||
          errorInfo
      );
    }

    throw new Error(error.message);
  };

  const processPatronSession: processPatronSession = (
    token,
    data,
    customRedirectRoute = ''
  ) => {
    const patronSession = {
      token,
      user: {
        externalSystemId: data.user.externalSystemId,
        ...data.user?.personal,
      },
    };

    sessionStorage.setItem(UserSession.patron, JSON.stringify(patronSession));
    setAxiosHeaders(patronSession);

    if (appContext?.setPatronSession) {
      appContext.setPatronSession(patronSession);
    } else {
      setPatronSession(patronSession);
    }

    navigate(customRedirectRoute || redirectRoute);
  };

  const directLoginMutation = useMutation((values: DirectLoginRequestBody) =>
    axios
      .post('/bl-users/login', {
        ...values,
      })
      .then(response => {
        processPatronSession(response.headers['x-okapi-token'], response.data);
      })
      .catch(handleError)
  );

  const loginMutation = useMutation((values: LoginRequestBody) => {
    const pathPrefix = isConsortiaLogin ? invokePathPrefix : '';

    return axios
      .post(`${pathPrefix}/opac-auth/login`, values, {
        withCredentials: isConsortiaLogin,
      })
      .then(response => {
        appContext.authenticateToken(response.data.okapiToken, redirectRoute);
      })
      .catch(handleError);
  });

  return {
    directLoginMutation,
    loginMutation,
    processPatronSession,
  };
};
